import { Button, SelectInput, SelectInputItem, Switch, TextField } from '@ads/components-react';
import { useEffect, useState } from 'react';
import { RichTextEditor, Skeleton } from '@/shared/ui/atoms';

/**
 * Opção de idioma exibida no select (par chave/valor).
 */
export interface LanguageOption {
  readonly key: string;
  readonly value: string;
}

/**
 * Payload emitido ao salvar.
 */
export interface LanguageFormPayload {
  readonly cultureInfo: string;
  /** Título da prova no idioma. Presente apenas quando `showTitle` está ativo. */
  readonly title?: string | null;
  readonly instruction: string | null;
  readonly active: boolean;
}

/**
 * Strings visíveis do formulário. São injetadas pela feature (via `t()`) para
 * manter o componente shared livre de acoplamento com um namespace de i18n.
 * Campos opcionais só são renderizados quando fornecidos.
 */
export interface LanguageFormLabels {
  readonly header: string;
  readonly subtitle?: string;
  readonly cultureInfo: string;
  readonly cultureInfoPlaceholder: string;
  /** Rótulo do campo de título. Necessário quando `showTitle` está ativo. */
  readonly title?: string;
  readonly titlePlaceholder?: string;
  readonly instruction: string;
  readonly instructionPlaceholder: string;
  readonly instructionHint?: string;
  readonly active: string;
  readonly activeHint?: string;
  readonly save: string;
  readonly cancel: string;
}

export interface LanguageFormProps {
  /** Idiomas selecionáveis. Em modo bloqueado, usado para resolver o rótulo atual. */
  readonly availableLangs: readonly LanguageOption[];
  /** Indica edição de um idioma existente (controla skeleton de detalhe e select bloqueado). */
  readonly isEditMode: boolean;
  /** Exibe o cartão de status ativo/inativo. Padrão: mesmo valor de `isEditMode`. */
  readonly showActive?: boolean;
  /** Exibe o campo de título da prova. Padrão: `false`. */
  readonly showTitle?: boolean;
  /** Exibe o campo de instruções da prova. Padrão: `true`. */
  readonly showInstruction?: boolean;
  /** Trava o select de idioma (não permite trocar). Padrão: mesmo valor de `isEditMode`. */
  readonly lockCultureInfo?: boolean;
  readonly initialCultureInfo?: string;
  readonly initialTitle?: string;
  readonly initialInstruction?: string;
  readonly initialActive?: boolean;
  readonly isLoadingLangs?: boolean;
  readonly isLoadingDetail?: boolean;
  readonly isSaving?: boolean;
  readonly labels: LanguageFormLabels;
  readonly onSave: (payload: LanguageFormPayload) => void;
  readonly onCancel: () => void;
}

/**
 * Formulário apresentacional de idioma de prova, compartilhado entre os fluxos
 * de gestão e de revisão. Não faz fetch nem mutação: recebe os dados e callbacks
 * por props, devolvendo o payload no `onSave`. A persistência (e o endpoint) é
 * responsabilidade do wrapper de cada feature.
 *
 * @param availableLangs - Idiomas selecionáveis
 * @param isEditMode - Edição de idioma existente
 * @param showActive - Exibe cartão de status ativo
 * @param lockCultureInfo - Trava a troca de idioma
 * @param labels - Strings visíveis injetadas pela feature
 * @param onSave - Recebe o payload ao salvar
 * @param onCancel - Acionado ao cancelar
 *
 * @example
 * ```tsx
 * <LanguageForm
 *   availableLangs={langs}
 *   isEditMode={false}
 *   labels={labels}
 *   onSave={payload => addLang(payload)}
 *   onCancel={close}
 * />
 * ```
 */
export function LanguageForm({
  availableLangs,
  isEditMode,
  showActive,
  showTitle = false,
  showInstruction = true,
  lockCultureInfo,
  initialCultureInfo = '',
  initialTitle = '',
  initialInstruction = '',
  initialActive = true,
  isLoadingLangs = false,
  isLoadingDetail = false,
  isSaving = false,
  labels,
  onSave,
  onCancel,
}: LanguageFormProps) {
  const isLocked = lockCultureInfo ?? isEditMode;
  const displaysActive = showActive ?? isEditMode;

  const [cultureInfo, setCultureInfo] = useState(initialCultureInfo);
  const [title, setTitle] = useState(initialTitle);
  const [instruction, setInstruction] = useState(initialInstruction);
  const [active, setActive] = useState(initialActive);

  // Re-sincroniza quando os valores iniciais chegam (ex.: detalhe carregado por API).
  useEffect(() => {
    setCultureInfo(initialCultureInfo);
  }, [initialCultureInfo]);
  useEffect(() => {
    setTitle(initialTitle);
  }, [initialTitle]);
  useEffect(() => {
    setInstruction(initialInstruction);
  }, [initialInstruction]);
  useEffect(() => {
    setActive(initialActive);
  }, [initialActive]);

  function handleSave() {
    if (!cultureInfo) return;
    onSave({
      cultureInfo,
      instruction: instruction || null,
      active,
      ...(showTitle ? { title: title || null } : {}),
    });
  }

  const lockedLabel = availableLangs.find(l => l.key === cultureInfo)?.value ?? cultureInfo;

  return (
    <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="border-b-hairline border-ctx-layout-border px-sm py-mini">
        <h3 className="ts-subtitle-xs text-ctx-content-title">{labels.header}</h3>
        {labels.subtitle && (
          <p className="ts-paragraph-xxs text-ctx-content-base mt-quark">{labels.subtitle}</p>
        )}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-mini px-sm py-mini">
        {/* Idioma */}
        {isLoadingLangs && !isEditMode ? (
          <Skeleton className="h-[52px] w-full rounded-sm" />
        ) : (
          <SelectInput
            label={labels.cultureInfo}
            placeholder={labels.cultureInfoPlaceholder}
            value={cultureInfo}
            onValueChange={setCultureInfo}
            size="md"
            fullWidth
            disabled={isLocked || availableLangs.length === 0}
          >
            {isLocked ? (
              <SelectInputItem value={cultureInfo} custom={{ root: 'text-nowrap' }}>
                {lockedLabel}
              </SelectInputItem>
            ) : (
              availableLangs.map(lang => (
                <SelectInputItem key={lang.key} value={lang.key} custom={{ root: 'text-nowrap' }}>
                  {lang.value}
                </SelectInputItem>
              ))
            )}
          </SelectInput>
        )}

        {/* Título */}
        {showTitle &&
          labels.title &&
          (isEditMode && isLoadingDetail ? (
            <Skeleton className="h-[52px] w-full rounded-sm" />
          ) : (
            <TextField
              label={labels.title}
              placeholder={labels.titlePlaceholder}
              value={title}
              onChange={e => setTitle(e.target.value)}
              fullWidth
            />
          ))}

        {/* Instruções */}
        {showInstruction &&
          (isEditMode && isLoadingDetail ? (
            <Skeleton className="h-[100px] w-full rounded-sm" />
          ) : (
            <div className="flex flex-col gap-nano">
              <span className="ts-heading-xxs !font-medium text-ctx-content-title">
                {labels.instruction}
              </span>
              <RichTextEditor
                placeholder={labels.instructionPlaceholder}
                value={instruction}
                onChange={value => setInstruction(value)}
                minHeight={120}
              />
              {labels.instructionHint && (
                <p className="ts-caption-xxs text-ctx-content-base">{labels.instructionHint}</p>
              )}
            </div>
          ))}

        {/* Status */}
        {displaysActive && (
          <div className="flex items-center justify-between rounded-sm border-hairline border-ctx-layout-border px-xs py-nano">
            <div className="flex flex-col">
              <span className="ts-paragraph-xs text-ctx-content-title">{labels.active}</span>
              {labels.activeHint && (
                <span className="ts-paragraph-xxs text-ctx-content-base">{labels.activeHint}</span>
              )}
            </div>
            <Switch checked={active} onCheckedChange={setActive} size="md" />
          </div>
        )}

        {/* Botões */}
        <div className="flex justify-end gap-nano">
          <Button hierarchy="secondary" size="md" onClick={onCancel}>
            {labels.cancel}
          </Button>
          <Button
            hierarchy="primary"
            size="md"
            onClick={handleSave}
            disabled={!cultureInfo || isLoadingLangs || isSaving}
            isLoading={isSaving}
          >
            {labels.save}
          </Button>
        </div>
      </div>
    </div>
  );
}
